/**
 * features/comments/RaiseFlagModal.tsx
 * ------------------------------------------------------------------
 * Lightweight modal used INLINE in ReviewWorkspace. Receives the
 * full context (year, term, class, student, target, subject) from
 * the caller — the editor only picks Reason + Notes. This is the
 * key UX shortcut: from 6 selections in the standalone screen down
 * to 2 here, because the system already knows everything that's
 * implied by "click the flag icon next to THIS comment".
 *
 * Writes via the shared raiseFlag() helper, so the resulting flag,
 * teacher notification and editor routing are IDENTICAL to what
 * the existing Comment Flags screen produces.
 * ------------------------------------------------------------------
 */
import { useState } from 'react';
import { Flag } from 'lucide-react';
import { useData } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/primitives';
import { FormModal } from '@/components/ui/glass';
import {
  raiseFlag,
  FLAG_REASONS,
  FLAG_TARGET_LABELS,
  type FlagReason,
} from '@/lib/raiseFlag';
import type { FlagTarget } from '@/types';

export interface RaiseFlagModalProps {
  open: boolean;
  onClose: () => void;
  // Pre-filled context from the click site
  year: string;
  term: string;
  classId: string;
  studentId: string;
  studentName: string;
  target: FlagTarget;
  subjectId?: string;
  subjectName?: string;
  teacherName?: string;
  /** What the comment being flagged actually says — shown for context. */
  commentPreview?: string;
}

export default function RaiseFlagModal({
  open,
  onClose,
  year,
  term,
  classId,
  studentId,
  studentName,
  target,
  subjectId,
  subjectName,
  teacherName,
  commentPreview,
}: RaiseFlagModalProps) {
  const { classes, students: allStudents, subjects, teachers, users } = useData();
  // Exclude 'pending' (Pastel-only, not yet enrolled).
  const students = allStudents.filter((s) => s.status !== 'pending');
  const { userData } = useAuth();
  const [reason, setReason] = useState<FlagReason>(FLAG_REASONS[0]);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  if (!open) return null;

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      const result = await raiseFlag({
        year,
        term,
        classId,
        studentId,
        target,
        reason,
        notes,
        subjectId,
        classes,
        students,
        subjects,
        teachers,
        users,
        flaggedById: userData?.uid ?? '',
        flaggedByName: userData?.displayName ?? 'Editor',
      });
      setDone(result.message);
      // brief confirmation then close
      setTimeout(() => {
        setDone(null);
        setReason(FLAG_REASONS[0]);
        setNotes('');
        onClose();
      }, 1100);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <FormModal
      open={open}
      onClose={onClose}
      icon={Flag}
      title="Raise a flag"
      subtitle="Notifies the teacher and the editor for this form."
      maxWidth="max-w-md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} loading={busy} disabled={!!done}>Raise flag</Button>
        </>
      }
    >
      {/* Context summary — proves the modal already knows everything */}
      <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3 mb-3 text-xs space-y-1">
        <div>
          <span className="text-gray-500">Student:</span>{' '}
          <span className="font-medium">{studentName}</span>
        </div>
        <div>
          <span className="text-gray-500">Flagging:</span>{' '}
          <span className="font-medium">
            {FLAG_TARGET_LABELS[target]}
            {subjectName ? ` — ${subjectName}` : ''}
          </span>
        </div>
        {teacherName && (
          <div>
            <span className="text-gray-500">Teacher:</span>{' '}
            <span className="font-medium">{teacherName}</span>
          </div>
        )}
        {commentPreview && (
          <div className="pt-1 italic text-gray-600 dark:text-gray-300 border-t border-gray-200 dark:border-gray-700 mt-2">
            “{commentPreview.length > 160 ? commentPreview.slice(0, 158) + '…' : commentPreview}”
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium">Reason</label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value as FlagReason)}
            className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all"
          >
            {FLAG_REASONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium">Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Anything the teacher / editor should know…"
            className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all resize-y"
          />
        </div>
      </div>

      {err && <p className="mt-3 text-sm text-red-600">{err}</p>}
      {done && <p className="mt-3 text-sm text-emerald-700 dark:text-emerald-300">{done}</p>}
    </FormModal>
  );
}
